import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { MapPin, Star, Clock, Users, Camera, Phone, Car, Trees, Droplets } from "lucide-react";

const ChoralDam = () => {
  const navigate = useNavigate();
  const damImage = "/Indore/nature/choraldam.jpg";
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "name": "Choral Dam",
    "description": "Beautiful dam and reservoir near Indore offering scenic views, boating, nature trails, and peaceful environment for weekend getaways",
    "image": damImage,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Choral Dam Road",
      "addressLocality": "Mhow-Neemuch Highway",
      "addressRegion": "Madhya Pradesh",
      "addressCountry": "India"
    },
    "openingHours": "Mo-Su 06:00-18:00",
    "priceRange": "₹20",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.2",
      "reviewCount": "800"
    }
  };

  const highlights = [
    {
      icon: <Droplets className="w-6 h-6" />,
      title: "Scenic Water Reservoir",
      description: "Beautiful dam with crystal clear water surrounded by lush green hills and natural landscape"
    },
    {
      icon: <Trees className="w-6 h-6" />,
      title: "Nature Trails",
      description: "Well-marked walking trails through forests and hills offering excellent views and wildlife spotting"
    },
    {
      icon: <Camera className="w-6 h-6" />,
      title: "Photography Paradise",
      description: "Perfect destination for nature photography with stunning sunrise, sunset, and landscape views"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Family Picnic Spot",
      description: "Ideal location for family outings, picnics, and weekend getaways from city life"
    }
  ];

  const visitInfo = [
    { label: "Entry Fee", value: "₹20 per person" },
    { label: "Timings", value: "6:00 AM - 6:00 PM (Daily)" },
    { label: "Boating", value: "Available (seasonal)" },
    { label: "Best Time", value: "October to March" },
    { label: "Duration", value: "4-6 hours" },
    { label: "Distance from Indore", value: "40 km (1 hour drive)" }
  ];

  const activities = [
    {
      title: "Boating",
      description: "Enjoy peaceful boat rides on the reservoir with scenic mountain backdrop",
      highlight: "Best during morning hours"
    },
    {
      title: "Nature Walking", 
      description: "Explore forest trails and discover local flora and fauna",
      highlight: "Early morning for wildlife"
    },
    {
      title: "Photography",
      description: "Capture stunning landscapes, sunsets, and natural beauty",
      highlight: "Golden hour lighting"
    },
    {
      title: "Picnicking",
      description: "Family-friendly spots with shaded areas and scenic views",
      highlight: "Designated picnic areas"
    },
    {
      title: "Bird Watching",
      description: "Spot various migratory and local bird species around the water",
      highlight: "Winter months best"
    },
    {
      title: "Camping",
      description: "Overnight camping experience under starlit skies (with permission)",
      highlight: "Advance booking required"
    }
  ];

  const nearbyAttractions = [
    { name: "Jam Gate", distance: "15 km", time: "20 mins" },
    { name: "Patalpani Waterfall", distance: "25 km", time: "35 mins" },
    { name: "Mandu", distance: "60 km", time: "1.5 hours" },
    { name: "Maheshwar", distance: "90 km", time: "2 hours" }
  ];

  const seasonalInfo = [
    {
      season: "Winter (Oct-Feb)",
      description: "Perfect weather with cool mornings and pleasant days. Best time for all activities.",
      activities: "Ideal for camping, trekking, and photography"
    },
    {
      season: "Summer (Mar-May)",
      description: "Hot days but early mornings and evenings are pleasant. Water levels may be lower.",
      activities: "Early morning visits, boating when available"
    },
    {
      season: "Monsoon (Jun-Sep)",
      description: "Heavy rains transform the landscape. Dam fills up but access may be restricted.",
      activities: "Limited access, check weather conditions"
    }
  ];

  const safetyTips = [
    "Always wear life jackets during boating activities",
    "Stay within designated swimming areas if allowed",
    "Carry sufficient water and snacks for day trips",
    "Inform someone about your travel plans",
    "Check weather conditions before visiting",
    "Respect local wildlife and maintain distance"
  ];

  return (
    <>
      <Helmet>
        <title>Choral Dam Indore - Nature Getaway | Boating & Photography | Aaoghumen</title>
        <meta name="description" content="Visit Choral Dam near Indore with Aaoghumen - enjoy scenic reservoir views, boating, nature trails, photography, and peaceful weekend getaways. Complete guide with activities and timings." />
        <meta name="keywords" content="choral dam indore, choral dam boating, nature spots near indore, weekend getaway indore, photography locations indore, aaoghumen choral dam, scenic places indore, family picnic spots indore" />
        
        <meta property="og:title" content="Choral Dam - Scenic Nature Getaway | Indore | Aaoghumen" />
        <meta property="og:description" content="Escape to the beautiful Choral Dam for scenic views, boating, nature trails, and perfect weekend relaxation away from city life." />
        <meta property="og:image" content={damImage} />
        <meta property="og:url" content="https://aaoghumen.com/destinations/indore/choral-dam" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Choral Dam - Nature Photography & Boating | Aaoghumen" />
        <meta name="twitter:description" content="Discover the serene beauty of Choral Dam with boating, nature trails, and stunning photography opportunities." />
        <meta name="twitter:image" content={damImage} />
        
        <link rel="canonical" href="https://aaoghumen.com/destinations/indore/choral-dam" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
        {/* Hero Section */}
        <div className="relative h-96 bg-gradient-to-r from-green-600 via-blue-500 to-teal-600 overflow-hidden">
          <img
            src={damImage}
            alt="Choral Dam scenic reservoir nature photography"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-80"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/30"></div>
          <div className="relative container mx-auto px-4 h-full flex items-center">
            <div className="text-white max-w-3xl">
              <h1 className="text-5xl font-bold mb-4">
                <span className="text-yellow-300">Choral Dam</span> - Nature's Paradise
              </h1>
              <p className="text-xl mb-6">
                Escape to the tranquil beauty of Choral Dam, where pristine waters meet lush green hills, offering the perfect retreat for nature lovers, photography enthusiasts, and families seeking peaceful moments.
              </p>
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Star className="w-5 h-5 text-yellow-400 mr-2" />
                  <span>4.2 Rating</span>
                </div>
                <div className="flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  <span>6 AM - 6 PM</span>
                </div>
                <div className="flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  <span>₹20 Entry</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-12">
          {/* About Section */}
          <section className="mb-16">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">About Choral Dam</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Choral Dam stands as one of the most beautiful and accessible natural destinations near Indore, offering a perfect blend of scenic beauty, recreational activities, and peaceful environment. Located approximately 40 kilometers from Indore city, this stunning reservoir is surrounded by rolling hills, dense forests, and rocky outcrops that create a picturesque landscape. The dam serves not only as a crucial water resource but also as a popular weekend getaway for city dwellers seeking respite from urban life.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                The area around Choral Dam is rich in biodiversity, featuring various species of birds, small mammals, and diverse plant life. The crystal-clear waters of the reservoir reflect the surrounding hills, creating mirror-like images that are particularly stunning during sunrise and sunset. The dam's strategic location along the Mhow-Neemuch highway makes it easily accessible, while its natural beauty and tranquil atmosphere provide the perfect setting for photography, nature walks, boating, and family picnics.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                With Aaoghumen, discover the hidden gems around Choral Dam, from the best photography spots to secret nature trails. Our local guides know the area intimately and can lead you to the most scenic viewpoints, help you spot wildlife, and ensure you experience the dam's beauty safely. Whether you're seeking adventure, relaxation, or simply a connection with nature, Choral Dam offers an unforgettable experience.
              </p>
            </div>
          </section>

          {/* Dam Highlights */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Nature Highlights</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {highlights.map((highlight, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="bg-green-100 p-3 rounded-lg mr-4">
                      {highlight.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">{highlight.title}</h3>
                  </div>
                  <p className="text-gray-600">{highlight.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Activities */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Activities & Experiences</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {activities.map((activity, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                  <h3 className="text-xl font-bold text-green-600 mb-3">{activity.title}</h3>
                  <p className="text-gray-600 mb-4 text-sm leading-relaxed">{activity.description}</p>
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <p className="text-blue-800 font-medium text-xs">{activity.highlight}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Seasonal Information */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Seasonal Guide</h2>
            <div className="grid md:grid-cols-1 gap-6">
              {seasonalInfo.map((season, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <h3 className="text-xl font-bold text-green-600 mb-4">{season.season}</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">{season.description}</p>
                  <div className="bg-green-100 p-3 rounded-lg">
                    <p className="text-green-800 font-medium text-sm">Recommended Activities: {season.activities}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Visit Information */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Visitor Information</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {visitInfo.map((info, index) => (
                  <div key={index} className="border-l-4 border-green-500 pl-4">
                    <h3 className="font-bold text-gray-800 mb-2">{info.label}</h3>
                    <p className="text-gray-600">{info.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Safety Tips */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Safety & Guidelines</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Safety Tips</h3>
                  <ul className="space-y-3">
                    {safetyTips.map((tip, index) => (
                      <li key={index} className="flex items-start text-gray-600">
                        <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">What to Bring</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span>Comfortable walking shoes and sun hat</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span>Camera for capturing scenic beauty</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span>Sufficient water and light snacks</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span>Sunscreen and insect repellent</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span>First aid kit for longer excursions</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Nearby Attractions */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Nearby Nature Destinations</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {nearbyAttractions.map((attraction, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 cursor-pointer">
                  <h3 className="text-lg font-bold text-gray-800 mb-2">{attraction.name}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-2 text-green-500" />
                      <span>{attraction.distance}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Car className="w-4 h-4 mr-2 text-blue-500" />
                      <span>{attraction.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* How to Reach */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">How to Reach</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Private Vehicle</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Route:</strong> Indore → Mhow Highway → Choral Dam (40 km)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Time:</strong> 1 hour drive from Indore city center</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Parking:</strong> Available near dam entrance</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Public Transport</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Bus:</strong> Take bus to Mhow, then local transport to dam</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Taxi:</strong> Hire taxi from Indore for comfortable journey</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Shared Cab:</strong> Group bookings available from city</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Contact and Plan Visit */}
          <section className="bg-gradient-to-r from-green-600 to-blue-600 rounded-2xl shadow-lg p-8 text-white">
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-4">Experience Nature's Beauty at Choral Dam</h2>
              <p className="text-xl mb-6">Discover scenic landscapes, enjoy water activities, and reconnect with nature</p>
              <div className="flex justify-center items-center space-x-8 mb-6">
                <div className="flex items-center">
                  <Phone className="w-5 h-5 mr-2" />
                  <span>+91 97139 08893</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  <span>40 km from Indore</span>
                </div>
              </div>
              <div className="flex justify-center space-x-4">
                <button 
                  onClick={() => navigate('/onboarding')}
                  className="bg-white text-green-600 font-bold py-3 px-6 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Plan Nature Trip
                </button>
                <button 
                  onClick={() => navigate('/onboarding')}
                  className="border-2 border-white text-white font-bold py-3 px-6 rounded-full hover:bg-white hover:text-green-600 transition-colors"
                >
                  Get Directions
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default ChoralDam;